/**
 * Back-office — Interventions (planning.manage). Liste de toutes les
 * interventions planifiées : dossier, client, exécutant (BCL + technicien, ou
 * partenaire), date prévue, statut. L'assignation se fait depuis le dossier ;
 * ici on consulte et on saute au dossier.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import {
  listInterventions,
  type Intervention,
} from '@/features/appointments/appointments-api';
import {
  appointmentStatusBadge,
  formatSlot,
} from '@/features/appointments/appointment-labels';

export function executorLabel(it: Intervention): string {
  if (it.executorKind === 'bcl')
    return `BCL · ${it.executorName ?? 'technicien'}`;
  if (it.executorKind === 'partenaire')
    return `Partenaire · ${it.executorName ?? ''}`.trim();
  return 'À affecter';
}

export function AdminInterventionsPage() {
  const interventions = useQuery({
    queryKey: ['admin', 'interventions'],
    queryFn: listInterventions,
  });

  return (
    <DashboardLayout variant="admin" title="Interventions">
      <Card>
        <CardHeader>
          <CardTitle>Interventions</CardTitle>
          <CardDescription>
            {interventions.data
              ? `${interventions.data.length} intervention(s) planifiée(s)`
              : 'Chargement…'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dossier</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Exécutant</TableHead>
                <TableHead>Date prévue</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {interventions.data?.length ? (
                interventions.data.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="font-medium">
                      {it.projectReference ?? '—'}
                    </TableCell>
                    <TableCell>{it.clientName ?? '—'}</TableCell>
                    <TableCell>{executorLabel(it)}</TableCell>
                    <TableCell className="text-sm">
                      {formatSlot(it.scheduledAt)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={appointmentStatusBadge[it.status].cls}
                      >
                        {appointmentStatusBadge[it.status].label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/admin/projects/${it.projectId}`}>
                          Voir le dossier
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-gray-500">
                    Aucune intervention planifiée.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
